/**
 * ⚖ The conversation DEEP REPORT — `assess-content`'s per-thread analysis.
 *
 * For a compound WhatsApp conversation, `assess-content` writes a second
 * sidecar beside the source: `sources/whatsapp/<stem>.assessment.md`. It is
 * NOT the `.info` evaluation (a short summary the AVALIAÇÃO pane already
 * shows) — it is the deep read of the conversation, broken into threads.
 *
 * Its body is THREADS + GAPS ONLY (`mission-ingest` `97bcb09bf`, operator
 * ruling 2026-08-11): it opens at `## Tópicos identificados (N)`, each thread a
 * `### N. <topic> — relevância <label>` with a period line, prose, an anchor
 * run and quotes, optionally closing with `## Lacunas`. The title, the repeated
 * summary, the rationale and the metadata header were all removed precisely
 * because this renders INSIDE the pane, under a `.info` summary that already
 * says those things.
 *
 * ## Why this module only derives a path
 *
 * Whether the report exists is answered by ATTEMPTING THE READ, never by
 * looking in the tree. `isUserFacingFile` filters only `.lexmeta.json`, so the
 * companions are absent from the tree because the RUNTIME keeps them out — a
 * tree lookup would answer "absent" for every report ever written. Same rule,
 * same reason, as `conversation-page.ts`.
 */

/** The suffix `assess-content` writes the deep report under. */
export const DEEP_REPORT_SUFFIX = ".assessment.md";

/** Companion suffix that is a stem decoration, not a real extension. */
const CONVERSATION_PAGE_SUFFIX = ".conversation.md";

/**
 * The deep-report path for a conversation, or null when the input is not a
 * plausible source path.
 *
 * Accepts EITHER the head's raw export (`…/conversa.txt`) or its assembled page
 * (`…/conversa.conversation.md`) and resolves both to the same
 * `…/conversa.assessment.md`. That second case is not hypothetical: the pane
 * sits under a viewer that may be showing the page rather than the head, and a
 * naive final-extension swap would ask for `conversa.conversation.assessment.md`,
 * a file that can never exist — the same silent never-matches failure that the
 * conversation page itself shipped with once.
 */
export function deepReportPathFor(path: string): string | null {
  const trimmed = path.trim();
  if (!trimmed) return null;
  // Already the report — never derive `.assessment.assessment.md`.
  if (trimmed.endsWith(DEEP_REPORT_SUFFIX)) return trimmed;

  const slash = trimmed.lastIndexOf("/");
  const name = slash >= 0 ? trimmed.slice(slash + 1) : trimmed;
  const dir = slash >= 0 ? trimmed.slice(0, slash + 1) : "";

  // The assembled page's suffix is TWO dotted parts; stripping one extension
  // would leave `conversa.conversation`.
  if (name.endsWith(CONVERSATION_PAGE_SUFFIX)) {
    return `${dir}${name.slice(0, -CONVERSATION_PAGE_SUFFIX.length)}${DEEP_REPORT_SUFFIX}`;
  }

  const dot = name.lastIndexOf(".");
  // A leading dot is a dotfile, not an extension.
  const stem = dot > 0 ? name.slice(0, dot) : name;
  return `${dir}${stem}${DEEP_REPORT_SUFFIX}`;
}
